import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Anchor, Users, Bus, GraduationCap, Settings2, Home, Layers, Sunrise } from "lucide-react";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Overview", icon: Home, testid: "nav-overview" },
  { to: "/groups", label: "Groups", icon: Users, testid: "nav-groups" },
  { to: "/roster", label: "Roster", icon: Layers, testid: "nav-roster" },
  { to: "/buses", label: "Buses", icon: Bus, testid: "nav-buses" },
  { to: "/cit", label: "CIT", icon: GraduationCap, testid: "nav-cit" },
  { to: "/ampm", label: "AM/PM", icon: Sunrise, testid: "nav-ampm" },
  { to: "/admin", label: "Admin", icon: Settings2, testid: "nav-admin" },
];

export default function Layout() {
  const location = useLocation();
  const isLetterView = /\/letter\//.test(location.pathname);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60_000,
  });
  const seasonYear = settings?.season_year || new Date().getFullYear();

  return (
    <div className="App">
      {!isLetterView && (
        <header
          data-testid="app-header"
          className="no-print sticky top-0 z-40 backdrop-blur bg-[#FDF6EC]/85 border-b border-[#E9DDC8]"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-6">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" data-testid="brand-link">
              <span className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#0B2545] text-[#FDF6EC] flex-none">
                <Anchor className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              <div className="leading-tight min-w-0">
                <div className="font-display text-base sm:text-xl font-bold text-[#0B2545] truncate">
                  Rolling River
                </div>
                <div className="font-mono-camp text-[9px] sm:text-[10px] tracking-widest text-[#13315C]/70 uppercase">
                  Tip Letters
                </div>
              </div>
            </Link>

            <nav className="flex flex-wrap items-center gap-1 justify-end">
              {NAV.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-sm font-semibold transition-colors border ${
                      isActive
                        ? "bg-[#0B2545] text-[#FDF6EC] border-[#0B2545]"
                        : "bg-transparent text-[#0B2545] border-transparent hover:border-[#0B2545]/25 hover:bg-white"
                    }`
                  }
                  title={label}
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="rope-divider" />
        </header>
      )}

      <main className={isLetterView ? "" : "max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10"}>
        <Outlet />
      </main>

      {!isLetterView && (
        <footer className="no-print border-t border-[#E9DDC8] mt-16">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#13315C]/70">
            <div className="font-mono-camp uppercase tracking-widest text-xs">
              Anchors Aweigh
            </div>
            <div>Rolling River Day Camp · Est. 1992</div>
          </div>
        </footer>
      )}
    </div>
  );
}
